import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import { Link } from 'react-router-dom';

interface MarkdownContentProps {
  source: string;
}

export function MarkdownContent({ source }: MarkdownContentProps) {
  return (
    <div className="max-w-none text-wolf-200 leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, [rehypeAutolinkHeadings, { behavior: 'wrap' }]]}
        components={{
          h1: ({ children, ...rest }) => (
            <h1 {...rest} className="text-3xl font-bold text-wolf-100 mt-8 mb-4">
              {children}
            </h1>
          ),
          h2: ({ children, ...rest }) => (
            <h2 {...rest} className="text-2xl font-semibold text-wolf-100 mt-8 mb-3">
              {children}
            </h2>
          ),
          h3: ({ children, ...rest }) => (
            <h3 {...rest} className="text-xl font-semibold text-wolf-100 mt-6 mb-2">
              {children}
            </h3>
          ),
          h4: ({ children, ...rest }) => (
            <h4 {...rest} className="text-lg font-semibold text-wolf-100 mt-4 mb-2">
              {children}
            </h4>
          ),
          p: ({ children, ...rest }) => (
            <p {...rest} className="text-wolf-200 my-3">
              {children}
            </p>
          ),
          ul: ({ children, ...rest }) => (
            <ul {...rest} className="list-disc pl-6 my-3 space-y-1 text-wolf-200">
              {children}
            </ul>
          ),
          ol: ({ children, ...rest }) => (
            <ol {...rest} className="list-decimal pl-6 my-3 space-y-1 text-wolf-200">
              {children}
            </ol>
          ),
          li: ({ children, ...rest }) => (
            <li {...rest} className="text-wolf-200">
              {children}
            </li>
          ),
          strong: ({ children, ...rest }) => (
            <strong {...rest} className="text-wolf-100 font-semibold">
              {children}
            </strong>
          ),
          em: ({ children, ...rest }) => (
            <em {...rest} className="text-wolf-100 italic">
              {children}
            </em>
          ),
          blockquote: ({ children, ...rest }) => (
            <blockquote
              {...rest}
              className="border-l-4 border-wolf-700 pl-4 my-4 text-wolf-300 italic"
            >
              {children}
            </blockquote>
          ),
          hr: ({ ...rest }) => <hr {...rest} className="my-6 border-wolf-700" />,
          code: ({ children, className, ...rest }) => {
            const isBlock = className?.includes('language-');
            if (isBlock) {
              return (
                <code {...rest} className={`${className ?? ''} text-wolf-100`}>
                  {children}
                </code>
              );
            }
            return (
              <code
                {...rest}
                className="bg-wolf-800 text-wolf-100 px-1.5 py-0.5 rounded text-sm font-mono"
              >
                {children}
              </code>
            );
          },
          pre: ({ children, ...rest }) => (
            <pre
              {...rest}
              className="bg-wolf-900 text-wolf-100 p-4 rounded-md overflow-x-auto my-4 text-sm font-mono border border-wolf-800"
            >
              {children}
            </pre>
          ),
          table: ({ children, ...rest }) => (
            <div className="overflow-x-auto my-4">
              <table {...rest} className="w-full text-sm border-collapse text-wolf-200">
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...rest }) => (
            <thead {...rest} className="border-b border-wolf-700">
              {children}
            </thead>
          ),
          tr: ({ children, ...rest }) => (
            <tr {...rest} className="border-b border-wolf-800">
              {children}
            </tr>
          ),
          th: ({ children, ...rest }) => (
            <th {...rest} className="text-left py-2 px-3 font-semibold text-wolf-100">
              {children}
            </th>
          ),
          td: ({ children, ...rest }) => (
            <td {...rest} className="py-2 px-3 text-wolf-200">
              {children}
            </td>
          ),
          a: ({ href, children, ...rest }) => {
            const linkClass = 'text-action hover:text-action-light underline';
            if (!href) {
              return (
                <a {...rest} className={linkClass}>
                  {children}
                </a>
              );
            }
            if (href.startsWith('/')) {
              return (
                <Link to={href} className={linkClass}>
                  {children}
                </Link>
              );
            }
            if (href.startsWith('#')) {
              return (
                <a href={href} className={linkClass}>
                  {children}
                </a>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                {children}
              </a>
            );
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
